/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  Optional,
  Renderer2,
  Self,
  ViewEncapsulation,
  Inject
} from '@angular/core';
import {coerceBooleanProperty, FocusOriginMonitor, Platform} from '../core';
import {mixinDisabled, CanDisable} from '../core/common-behaviors/disabled';
import {CanColor, mixinColor} from '../core/common-behaviors/color';
/**
 * Default color palette for round buttons (md-fab and md-mini-fab)
 */
const DEFAULT_ROUND_BUTTON_COLOR = 'accent';
/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MdButtonCssMatStyler {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'button[md-button], button[mat-button], a[md-button], a[mat-button]',
  host: {'class': 'mat-button'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdButtonCssMatStyler_tsickle_Closure_declarations() {
/** @type {?} */
MdButtonCssMatStyler.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdButtonCssMatStyler.ctorParameters;
}

/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MdRaisedButtonCssMatStyler {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector:
      'button[md-raised-button], button[mat-raised-button], ' +
      'a[md-raised-button], a[mat-raised-button]',
  host: {'class': 'mat-raised-button'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdRaisedButtonCssMatStyler_tsickle_Closure_declarations() {
/** @type {?} */
MdRaisedButtonCssMatStyler.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdRaisedButtonCssMatStyler.ctorParameters;
}

/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MdIconButtonCssMatStyler {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector:
      'button[md-icon-button], button[mat-icon-button], a[md-icon-button], a[mat-icon-button]',
  host: {'class': 'mat-icon-button'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdIconButtonCssMatStyler_tsickle_Closure_declarations() {
/** @type {?} */
MdIconButtonCssMatStyler.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdIconButtonCssMatStyler.ctorParameters;
}

/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MdFab {
/**
 * @param {?} button
 * @param {?} anchor
 */
constructor(   button: MdButton,
                 anchor: MdAnchor) {
    // Set the default color palette for the md-fab components.
    (button || anchor).color = DEFAULT_ROUND_BUTTON_COLOR;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'button[md-fab], button[mat-fab], a[md-fab], a[mat-fab]',
  host: {'class': 'mat-fab'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdButton, decorators: [{ type: Self }, { type: Optional }, { type: Inject, args: [forwardRef(() => MdButton), ] }, ]},
{type: MdAnchor, decorators: [{ type: Self }, { type: Optional }, { type: Inject, args: [forwardRef(() => MdAnchor), ] }, ]},
];
}

function MdFab_tsickle_Closure_declarations() {
/** @type {?} */
MdFab.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdFab.ctorParameters;
}

/**
 * Directive that targets mini-fab buttons and anchors. It's used to apply the `mat-` class
 * to all mini-fab buttons and also is responsible for setting the default color palette.
 * \@docs-private
 */
export class MdMiniFab {
/**
 * @param {?} button
 * @param {?} anchor
 */
constructor(   button: MdButton,
                 anchor: MdAnchor) {
    // Set the default color palette for the md-mini-fab components.
    (button || anchor).color = DEFAULT_ROUND_BUTTON_COLOR;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'button[md-mini-fab], button[mat-mini-fab], a[md-mini-fab], a[mat-mini-fab]',
  host: {'class': 'mat-mini-fab'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdButton, decorators: [{ type: Self }, { type: Optional }, { type: Inject, args: [forwardRef(() => MdButton), ] }, ]},
{type: MdAnchor, decorators: [{ type: Self }, { type: Optional }, { type: Inject, args: [forwardRef(() => MdAnchor), ] }, ]},
];
}

function MdMiniFab_tsickle_Closure_declarations() {
/** @type {?} */
MdMiniFab.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdMiniFab.ctorParameters;
}

export class MdButtonBase {
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(public _renderer: Renderer2,
public _elementRef: ElementRef) {}
}

function MdButtonBase_tsickle_Closure_declarations() {
/** @type {?} */
MdButtonBase.prototype._renderer;
/** @type {?} */
MdButtonBase.prototype._elementRef;
}

export const /** @type {?} */ _MdButtonMixinBase = mixinColor(mixinDisabled(MdButtonBase));
/**
 * Material design button.
 */
export class MdButton extends _MdButtonMixinBase implements OnDestroy, CanDisable, CanColor {
/**
 * Whether the button is round.
 */
_isRoundButton: boolean = this._hasAttributeWithPrefix('fab', 'mini-fab');
/**
 * Whether the button is icon button.
 */
_isIconButton: boolean = this._hasAttributeWithPrefix('icon-button');
/**
 * Whether the ripple effect on click should be disabled.
 */
private _disableRipple: boolean = false;
/**
 * Whether the ripple effect for this button is disabled.
 * @return {?}
 */
get disableRipple() { return this._disableRipple; }
/**
 * @param {?} v
 * @return {?}
 */
set disableRipple(v) { this._disableRipple = coerceBooleanProperty(v); }
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} _platform
 * @param {?} _focusOriginMonitor
 */
constructor(renderer: Renderer2,
              elementRef: ElementRef,
private _platform: Platform,
private _focusOriginMonitor: FocusOriginMonitor) {
    super(renderer, elementRef);
    this._focusOriginMonitor.monitor(this._elementRef.nativeElement, this._renderer, true);
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._focusOriginMonitor.stopMonitoring(this._elementRef.nativeElement);
  }
/**
 * Focuses the button.
 * @return {?}
 */
focus(): void {
    this._getHostElement().focus();
  }
/**
 * @return {?}
 */
_getHostElement() {
    return this._elementRef.nativeElement;
  }
/**
 * @return {?}
 */
_isRippleDisabled() {
    return this.disableRipple || this.disabled;
  }
/**
 * Gets whether the button has one of the given attributes
 * with either an 'md-' or 'mat-' prefix.
 * @param {...?} unprefixedAttributeNames
 * @return {?}
 */
_hasAttributeWithPrefix(...unprefixedAttributeNames: string[]) {
    // If not on the browser, say that there are none of the attributes present.
    // Since these only affect how the ripple displays (and ripples only happen on the client),
    // detecting these attributes isn't necessary when not on the browser.
    if (!this._platform.isBrowser) {
      return false;
    }

    return unprefixedAttributeNames.some(suffix => {
      const /** @type {?} */ el = this._getHostElement();

      return el.hasAttribute('md-' + suffix) || el.hasAttribute('mat-' + suffix);
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'button[md-button], button[md-raised-button], button[md-icon-button],' +
            'button[md-fab], button[md-mini-fab],' +
            'button[mat-button], button[mat-raised-button], button[mat-icon-button],' +
            'button[mat-fab], button[mat-mini-fab]',
  host: {
    '[disabled]': 'disabled || null',
  },
  templateUrl: 'button.html',
  styleUrls: ['button.css'],
  inputs: ['disabled', 'color'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: ElementRef, },
{type: Platform, },
{type: FocusOriginMonitor, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'disableRipple': [{ type: Input },],
};
}

function MdButton_tsickle_Closure_declarations() {
/** @type {?} */
MdButton.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdButton.ctorParameters;
/** @type {?} */
MdButton.propDecorators;
/**
 * Whether the button is round.
 * @type {?}
 */
MdButton.prototype._isRoundButton;
/**
 * Whether the button is icon button.
 * @type {?}
 */
MdButton.prototype._isIconButton;
/**
 * Whether the ripple effect on click should be disabled.
 * @type {?}
 */
MdButton.prototype._disableRipple;
/** @type {?} */
MdButton.prototype._platform;
/** @type {?} */
MdButton.prototype._focusOriginMonitor;
}

/**
 * Raised Material design button.
 */
export class MdAnchor extends MdButton {
/**
 * @param {?} platform
 * @param {?} focusOriginMonitor
 * @param {?} elementRef
 * @param {?} renderer
 */
constructor(
      platform: Platform,
      focusOriginMonitor: FocusOriginMonitor,
      elementRef: ElementRef,
      renderer: Renderer2) {
    super(renderer, elementRef, platform, focusOriginMonitor);
  }
/**
 * \@docs-private
 * @return {?}
 */
get tabIndex(): number {
    return this.disabled ? -1 : 0;
  }
/**
 * @return {?}
 */
get _isAriaDisabled(): string {
    return this.disabled ? 'true' : 'false';
  }
/**
 * @param {?} event
 * @return {?}
 */
_haltDisabledEvents(event: Event) {
    // A disabled button shouldn't apply any actions
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: `a[md-button], a[md-raised-button], a[md-icon-button], a[md-fab], a[md-mini-fab],
             a[mat-button], a[mat-raised-button], a[mat-icon-button], a[mat-fab], a[mat-mini-fab]`,
  host: {
    '[attr.disabled]': 'disabled || null',
    '[attr.aria-disabled]': '_isAriaDisabled',
    '(click)': '_haltDisabledEvents($event)',
  },
  inputs: ['disabled', 'color'],
  templateUrl: 'button.html',
  styleUrls: ['button.css'],
  encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Platform, },
{type: FocusOriginMonitor, },
{type: ElementRef, },
{type: Renderer2, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'tabIndex': [{ type: HostBinding, args: ['tabIndex', ] },],
};
}

function MdAnchor_tsickle_Closure_declarations() {
/** @type {?} */
MdAnchor.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdAnchor.ctorParameters;
/** @type {?} */
MdAnchor.propDecorators;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
